import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { FiltersQueryTransformer } from 'src/app/services/filters/filtersQueryTransformer.ts/filtersQueryTransformer';
import { IPageFilters } from 'src/app/models/filters/interfaces/pageFilters.interface';
import { MultiSelectFilters } from 'src/app/models/filters/implementations/filtersTypes/multiFilter.model';
import { FilterTypesEnum } from 'src/app/models/filters/filterTypes.enum';
import { ISelectOption, SeveritySelectOptions } from 'src/app/models/selectOptions';
import { SectionType } from '../violation-rules.component';
import { OwaspSelectOptionAll, OwaspSelectOptions } from 'src/app/models/selectOptions/owaspSelectOptions.model';
import { computed, Signal, signal, WritableSignal } from '@angular/core';
import {
  OwaspLlmSelectOptionAll,
  OwaspLlmSelectOptions,
} from 'src/app/models/selectOptions/owaspLlmSelectOptions.model';
import {
  OwaspAsiSelectOptionAll,
  OwaspAsiSelectOptions,
} from 'src/app/models/selectOptions/owaspAsiSelectOptions.model';
import {
  OwaspNhiSelectOptionAll,
  OwaspNhiSelectOptions,
} from 'src/app/models/selectOptions/owaspNhiSelectOptions.model';
import { AttackSelectOptionAll, AttackSelectOptions } from 'src/app/models/selectOptions/attackSelectOptions.model';
import {
  GenAiMatrixSelectOptionsAll,
  GenAiMatrixSelectOptions,
} from 'src/app/models/selectOptions/genAiMatrixSelectOptions.model';
import { ViolationRulesFilter } from './violation-rules-vertical-filters.component';
import { ISelectFilter } from 'src/app/models/filters/interfaces/selectFilter.interface';
import { ISelectOptionGroups } from 'src/app/models/selectOptions/selectOptionGroups.interface';
import { Label } from 'src/app/labels/englishLabels';
import { ViolationRuleStatusSelectOptions } from 'src/app/models/selectOptions/violationRuleStatusSelectOptions.model';
import { EventDrivenSupportSelectOptions } from 'src/app/models/selectOptions/eventDrivenSupportSelectOptions.model';
import { DetectionRulesHttpService } from 'src/app/services/httpServices/governance/detectionRulesHttpService/detection-rules-http.service';
import { RuleTagDisplayNamePipe } from 'src/app/pipes/rule-tag-display-name/rule-tag-display-name.pipe';
import { getAiServiceLogoPath, getAiServiceDisplayName } from 'src/app/common/aiServiceUtils';
import { FindingAiService } from 'src/app/models/api/agentStep.model';

export type ViolationRulesFiltersValues = {
  group: string[];
  tags: string[];
  status: string[];
  severity: string[];
  aiService: string[];
  eventDrivenSupport: string[];
};
export type OptionGroup = { filter: ViolationRulesFilter; group: ISelectOptionGroups };
type Filters = Record<ViolationRulesFilter, { type: ViolationRulesFilter; instance: ISelectFilter }>;
type CommonFilters = Record<
  ViolationRulesFilter.STATUS | ViolationRulesFilter.SEVERITY | ViolationRulesFilter.LIGHTNING_SUPPORT,
  {
    type: ViolationRulesFilter.STATUS | ViolationRulesFilter.SEVERITY | ViolationRulesFilter.LIGHTNING_SUPPORT;
    instance: ISelectFilter;
  }
>;

export class ViolationRulesFiltersModel implements IPageFilters<ViolationRulesFiltersValues, ViolationRulesFilter> {
  public onUpdate$: BehaviorSubject<ViolationRulesFiltersValues>;
  public allOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);

  public filters: WritableSignal<Filters | null> = signal(null);
  public optionsGroups: Signal<OptionGroup[]>;

  public sortedColumn$: BehaviorSubject<SortColumn | undefined> = new BehaviorSubject<SortColumn | undefined>(
    undefined
  );

  private _activeSection: SectionType | null = null;
  private readonly _sectionSpecificOptions: Record<
    SectionType,
    { allOptionId: string; options: ISelectFilter } | null
  > = {
    [SectionType.OWASP_ASI]: {
      allOptionId: OwaspAsiSelectOptionAll.id,
      options: new MultiSelectFilters([OwaspAsiSelectOptionAll, ...OwaspAsiSelectOptions]),
    },
    [SectionType.OWASP_NHI]: {
      allOptionId: OwaspNhiSelectOptionAll.id,
      options: new MultiSelectFilters([OwaspNhiSelectOptionAll, ...OwaspNhiSelectOptions]),
    },
    [SectionType.OWASP]: {
      allOptionId: OwaspSelectOptionAll.id,
      options: new MultiSelectFilters([OwaspSelectOptionAll, ...OwaspSelectOptions]),
    },
    [SectionType.OWASP_LLM]: {
      allOptionId: OwaspLlmSelectOptionAll.id,
      options: new MultiSelectFilters([OwaspLlmSelectOptionAll, ...OwaspLlmSelectOptions]),
    },
    [SectionType.MITRE]: {
      allOptionId: AttackSelectOptionAll.id,
      options: new MultiSelectFilters([AttackSelectOptionAll, ...AttackSelectOptions]),
    },
    [SectionType.GEN_AI_MATRIX]: {
      allOptionId: GenAiMatrixSelectOptionsAll.id,
      options: new MultiSelectFilters([GenAiMatrixSelectOptionsAll, ...GenAiMatrixSelectOptions]),
    },
    [SectionType.NONE]: null,
  };

  private readonly _commonOptions: CommonFilters = {
    [ViolationRulesFilter.STATUS]: {
      type: ViolationRulesFilter.STATUS,
      instance: new MultiSelectFilters([...ViolationRuleStatusSelectOptions]),
    },
    [ViolationRulesFilter.SEVERITY]: {
      type: ViolationRulesFilter.SEVERITY,
      instance: new MultiSelectFilters([...SeveritySelectOptions]),
    },
    [ViolationRulesFilter.LIGHTNING_SUPPORT]: {
      type: ViolationRulesFilter.LIGHTNING_SUPPORT,
      instance: new MultiSelectFilters([...EventDrivenSupportSelectOptions]),
    },
  };

  private _tagFilter: { type: ViolationRulesFilter.TAGS; instance: MultiSelectFilters } | null = null;
  private _aiServiceFilter: { type: ViolationRulesFilter.AI_SERVICE; instance: MultiSelectFilters } | null = null;

  private readonly _filterNames: Record<string, string> = {
    [ViolationRulesFilter.GROUP_NAME]: Label.get('policyPage.policy.violationRules.groupName'),
    [ViolationRulesFilter.TAGS]: Label.get('policyPage.policy.violationRules.tags'),
    [ViolationRulesFilter.STATUS]: Label.get('policyPage.policy.violationRules.status'),
    [ViolationRulesFilter.SEVERITY]: Label.get('policyPage.policy.violationRules.severity'),
    [ViolationRulesFilter.AI_SERVICE]: Label.get('policyPage.policy.violationRules.aiService'),
    [ViolationRulesFilter.LIGHTNING_SUPPORT]: Label.get('policyPage.policy.violationRules.lightningSupport'),
  };

  private _detectionRulesHttpService: DetectionRulesHttpService | null = null;

  private readonly _filterTypeToViolationRulesFilter: Record<string, ViolationRulesFilter> = {
    [FilterTypesEnum.RuleTag]: ViolationRulesFilter.TAGS,
    [FilterTypesEnum.ViolationRuleStatus]: ViolationRulesFilter.STATUS,
    [FilterTypesEnum.Severity]: ViolationRulesFilter.SEVERITY,
  };

  constructor(private readonly _router: Router) {
    this.onUpdate$ = new BehaviorSubject(this.getSelectedFilters());

    this.optionsGroups = computed(() => {
      const filters = this.filters();
      if (!filters) {
        return [];
      }

      return Object.values(filters).reduce((prev, filter) => {
        const group: ISelectOptionGroups = {
          selectedCount: 0,
          displayName: this._filterNames[filter.type],
          options: filter.instance.getOptions$().value,
          isDynamic: false,
          isNew: false,
        };
        prev.push({ filter: filter.type, group } as OptionGroup);
        return prev;
      }, [] as OptionGroup[]);
    });
  }

  public initializeFilters(
    detectionRulesHttpService: DetectionRulesHttpService,
    integrationId: string,
    policyId: string
  ): void {
    this._detectionRulesHttpService = detectionRulesHttpService;
    this._loadRulesFilters(integrationId, policyId);
  }

  public sectionChange(activeSection: SectionType): void {
    this._activeSection = activeSection;
    this._initializeFilters(activeSection);
    this.onUpdate$.next(this.getSelectedFilters());
  }

  public getAllFiltersOptions(): BehaviorSubject<ISelectOption[]> {
    return this.allOptions$;
  }

  public getAllSelectedOptions(): ISelectOption[] {
    const filters = this.filters();
    if (!filters) {
      return [];
    }
    const selectedOptions: ISelectOption[] = [];
    Object.values(filters).forEach((filter) => {
      const selected = filter.instance.getSelectedOptions();
      if (!selected) {
        return;
      }
      if (selected instanceof Array) {
        selectedOptions.push(...selected);
      } else {
        selectedOptions.push(selected);
      }
    });
    return selectedOptions;
  }

  public getSelectedFilters(): ViolationRulesFiltersValues {
    const filters = this.filters();
    if (!filters) {
      return {} as ViolationRulesFiltersValues;
    }
    return {
      group: (filters[ViolationRulesFilter.GROUP_NAME]?.instance.getSelectedFiltersValues() as string[]) ?? [],
      tags: (filters[ViolationRulesFilter.TAGS]?.instance.getSelectedFiltersValues() as string[]) ?? [],
      status: (filters[ViolationRulesFilter.STATUS]?.instance.getSelectedFiltersValues() as string[]) ?? [],
      severity: (filters[ViolationRulesFilter.SEVERITY]?.instance.getSelectedFiltersValues() as string[]) ?? [],
      aiService: (filters[ViolationRulesFilter.AI_SERVICE]?.instance.getSelectedFiltersValues() as string[]) ?? [],
      eventDrivenSupport:
        (filters[ViolationRulesFilter.LIGHTNING_SUPPORT]?.instance.getSelectedFiltersValues() as string[]) ?? [],
    };
  }

  public getDynamicFilters(): string[] {
    return [];
  }

  public getNewFilters(): string[] {
    return [];
  }

  public updateFilterFromUserSelection(
    filterToUpdate: ViolationRulesFilter | undefined,
    value: ISelectOption[] | Date | boolean | string | undefined
  ) {
    const activeSection = this._activeSection;
    const filters = this.filters();

    if (activeSection === null || !filters) {
      return;
    }

    let needUpdate = false;

    switch (filterToUpdate) {
      case ViolationRulesFilter.GROUP_NAME:
        needUpdate = this._changeGroupName(
          value as ISelectOption[],
          activeSection,
          filters[ViolationRulesFilter.GROUP_NAME].instance as MultiSelectFilters
        );
        break;
      case ViolationRulesFilter.TAGS:
      case ViolationRulesFilter.STATUS:
      case ViolationRulesFilter.SEVERITY:
      case ViolationRulesFilter.LIGHTNING_SUPPORT:
        this._updateAllOptions();
        needUpdate = filters[filterToUpdate].instance.updateIfNeeded(value as ISelectOption[]);
        break;

      case ViolationRulesFilter.AI_SERVICE:
        if (filters[filterToUpdate]) {
          this._updateAllOptions();
          needUpdate = filters[filterToUpdate].instance.updateIfNeeded(value as ISelectOption[]);
        }
        break;

      default:
        console.warn('Unhandled filter');
    }

    if (needUpdate) {
      this._handleSortedColumnOnFilter();
      const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
      this._router.navigate([], {
        queryParams: { q: filtersQuery },
        queryParamsHandling: 'merge',
      });
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  public removeSelectedOptionsIfExist(options: ISelectOption[]): void {
    const filters = this.filters();
    if (!filters) {
      return;
    }

    let needUpdate = false;

    // Group options by their filter type (category), mapping FilterTypesEnum to ViolationRulesFilter
    const optionsByFilter: { [key: string]: ISelectOption[] } = {};
    options.forEach((option) => {
      if (option.filterType) {
        const filterTypeKey = option.filterType.toString();
        const violationRulesFilter = this._filterTypeToViolationRulesFilter[filterTypeKey];
        if (violationRulesFilter) {
          if (!(violationRulesFilter in optionsByFilter)) {
            optionsByFilter[violationRulesFilter] = [];
          }
          optionsByFilter[violationRulesFilter].push(option);
        }
      }
    });

    // Remove options from each filter
    Object.keys(optionsByFilter).forEach((filterType) => {
      const filter = filters[filterType as ViolationRulesFilter];
      if (filter) {
        const currentSelected = filter.instance.getSelectedOptions();
        if (!currentSelected) {
          return;
        }
        const selectedArray = Array.isArray(currentSelected) ? currentSelected : [currentSelected];
        const optionsToRemove = new Set(optionsByFilter[filterType].map((opt: ISelectOption) => opt.id));
        const newSelected = selectedArray.filter((opt: ISelectOption) => !optionsToRemove.has(opt.id));

        if (filter.instance.updateIfNeeded(newSelected)) {
          needUpdate = true;
        }
      }
    });

    if (needUpdate) {
      this._updateAllOptions();
      const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
      this._router.navigate([], {
        queryParams: { q: filtersQuery },
        queryParamsHandling: 'merge',
      });
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  public getFilterOptionsIfExist$(filter: FilterTypesEnum): BehaviorSubject<ISelectOption[]> {
    switch (filter) {
      default:
        throw new Error(`ViolationsFiltersModel getFilterOptionsIfExist: ${filter} not supported`);
    }
  }

  public getFilterSelectedIfExist(filter: FilterTypesEnum): string[] {
    switch (filter) {
      default:
        throw new Error(`ViolationsFiltersModel getFilterSelectedIfExist: ${filter} not supported`);
    }
  }

  public updateFiltersByKeyValue(keyValueFilters: { [key: string]: string[] }, updateQueryParams = false): void {
    const filters = this.filters();
    if (!filters) {
      return;
    }

    let needUpdate = false;

    // If empty object is passed, clear all filters
    if (Object.keys(keyValueFilters).length === 0) {
      Object.values(filters).forEach((filter) => {
        if (filter.instance.updateIfNeeded([])) {
          needUpdate = true;
        }
      });
    }

    if (needUpdate) {
      this._updateAllOptions();
      if (updateQueryParams) {
        const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
        this._router.navigate([], {
          queryParams: { q: filtersQuery },
          queryParamsHandling: 'merge',
        });
      }
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  public restoreSortColumnToDefaultIfNeeded() {}

  private _initializeFilters(activeSection: SectionType) {
    let filters: Filters = {} as Filters;
    const sectionSpecific = this._sectionSpecificOptions[activeSection];
    if (sectionSpecific) {
      filters[ViolationRulesFilter.GROUP_NAME] = {
        type: ViolationRulesFilter.GROUP_NAME,
        instance: sectionSpecific.options,
      };
    }

    this._addDynamicFilters(filters);

    filters = {
      ...filters,
      ...this._commonOptions,
    };

    this.filters.set(filters);

    this._updateAllOptions();
  }

  private _addDynamicFilters(filters: Filters): void {
    if (this._tagFilter) {
      filters[ViolationRulesFilter.TAGS] = this._tagFilter;
    }
    if (this._aiServiceFilter) {
      filters[ViolationRulesFilter.AI_SERVICE] = this._aiServiceFilter;
    }
  }

  private _updateAllOptions() {
    const filters = this.filters();
    if (filters) {
      this.allOptions$.next(Object.values(filters).flatMap((filter) => filter.instance.getOptions$().value ?? []));
    }
  }

  private _handleSortedColumnOnFilter() {
    if (this.sortedColumn$.value === undefined) {
      this.restoreSortColumnToDefaultIfNeeded();
    }
  }

  private _changeGroupName(value: ISelectOption[], activeSection: SectionType, filters: MultiSelectFilters) {
    const includesAllOption = value.find(
      (option) => option.id === this._sectionSpecificOptions[activeSection]?.allOptionId
    );
    const previousValue = filters.getSelectedOptions();
    const previousValueIncludesAllOption = previousValue.find(
      (option) => option.id === this._sectionSpecificOptions[activeSection]?.allOptionId
    );
    const allPreviousOptions = filters.getOptions$().value;

    if (includesAllOption && !previousValueIncludesAllOption) {
      value = filters.getOptions$().value;
    }
    if (previousValueIncludesAllOption && !includesAllOption) {
      value = [];
    }
    if (includesAllOption && value.length !== allPreviousOptions.length) {
      value = value.filter((option) => option.id !== this._sectionSpecificOptions[activeSection]?.allOptionId);
    }

    if (value.length == 0) {
      return filters.updateIfNeeded([]);
    } else {
      this._updateAllOptions();
      return filters.updateIfNeeded(value);
    }
  }

  private _loadRulesFilters(integrationId: string, policyId: string): void {
    if (!this._detectionRulesHttpService) {
      return;
    }

    this._detectionRulesHttpService.getRulesFilters(integrationId, policyId).subscribe({
      next: (response) => {
        // Initialize tags filter if we have tags
        if (response.tags && response.tags.length > 0) {
          const tagOptions = this._transformTagsToSelectOptions(response.tags);
          this._tagFilter = {
            type: ViolationRulesFilter.TAGS,
            instance: new MultiSelectFilters(tagOptions),
          };
        } else {
          this._tagFilter = null;
        }

        // Initialize AI service filter if we have AI services
        if (response.aiServices && response.aiServices.length > 0) {
          const aiServiceOptions = this._transformAiServicesToSelectOptions(response.aiServices);
          this._aiServiceFilter = {
            type: ViolationRulesFilter.AI_SERVICE,
            instance: new MultiSelectFilters(aiServiceOptions),
          };
        } else {
          this._aiServiceFilter = null;
        }

        // Re-initialize filters if section is already active
        if (this._activeSection !== null) {
          this._initializeFilters(this._activeSection);
        }
      },
      error: () => {
        // Don't show filters on error
        this._tagFilter = null;
        this._aiServiceFilter = null;
      },
    });
  }

  private _transformTagsToSelectOptions(tags: string[]): ISelectOption[] {
    const pipe = new RuleTagDisplayNamePipe();
    return tags.map((tag) => ({
      id: `${FilterTypesEnum.RuleTag}-${tag}`,
      value: tag,
      displayName: pipe.transform(tag),
      categoryName: FilterTypesEnum.RuleTag.toString(),
      filterType: FilterTypesEnum.RuleTag,
      withoutBeautifyDisplayName: true,
    }));
  }

  private _transformAiServicesToSelectOptions(aiServices: string[]): ISelectOption[] {
    return aiServices
      .map((service) => {
        let displayName: string;
        let iconPath: string | undefined;

        try {
          displayName = getAiServiceDisplayName(service as FindingAiService);
          iconPath = getAiServiceLogoPath(service as FindingAiService);
        } catch {
          // Fallback for unknown services
          displayName = service;
          iconPath = undefined;
        }

        return {
          id: `${FilterTypesEnum.AiService}-${service}`,
          value: service,
          displayName,
          categoryName: FilterTypesEnum.AiService.toString(),
          filterType: FilterTypesEnum.AiService,
          iconPath,
          withoutBeautifyDisplayName: true,
        };
      })
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }
}
